// /messages/requests (R3-R9). The message-request inbox: every pending
// private chat waiting on the signed-in fan's approval, newest first, with a
// one-tap Approve/Ignore per row and a body-tap preview.
//
// Reached from the pinned Requests row at the top of the Messages list
// (`MessagesListBody`'s `onSelectRequests`, wired in `routes/messages.tsx`),
// and by direct URL. Approving a request, from its row or from the preview
// dialog, replaces this screen with the real conversation at
// `/messages?conversationId=<chatId>` (ISSUE-1050).
//
// The Messages tab's own badge chrome is NOT this screen's job, that is
// ISSUE-326's, per the same plan's section 1.2 correction.
import {
  useMessageRequestApprove,
  useMessageRequestDecline,
  useMessageRequestList,
} from "@sportsmet/messaging-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../errors/apiErrorBus";
import { RequestConversationPanel } from "../features/messages/RequestConversationPanel";
import { RequestRow } from "../features/messages/RequestRow";
import { resolveRequestsHeaderTitle } from "../features/messages/requestsFormatting";
import {
  closeOverlay,
  openOverlay,
  type typeOverlaySearch,
} from "../routeState/overlay";
import { coerceOptionalString } from "../routeState/searchParams";
import { SectionEmptyState, SectionScreen } from "../shell/SectionScreen";

const REQUEST_PREVIEW_DIALOG = "request-preview";
const INTRO_SEEN_STORAGE_KEY = "sm.messages.requestsIntroSeen";

// Verbatim from .agents/design-prompts/51-message-requests-and-moderation.md, do not paraphrase.
const INTRO_TEXT =
  "A message from someone new waits here until you approve them. That includes people in your pools.";
const INTRO_DISCLOSURE_LABEL = "Why do I see this?";

const EMPTY_HEADLINE = "No requests";
const EMPTY_BODY =
  "When someone messages you for the first time, it shows up here.";

export type typeMessagesRequestsSearch = typeOverlaySearch & {
  chatId?: string | undefined;
};

export function validateMessagesRequestsSearch(
  search: Record<string, unknown>,
): typeMessagesRequestsSearch {
  return {
    chatId: coerceOptionalString(search.chatId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

function readIntroSeen(): boolean {
  try {
    return window.localStorage.getItem(INTRO_SEEN_STORAGE_KEY) === "1";
  } catch {
    // Storage can throw in a locked-down environment (private browsing with
    // storage disabled). Treat that the same as "never seen" rather than
    // crashing the screen over a cosmetic disclosure.
    return false;
  }
}

function markIntroSeen(): void {
  try {
    window.localStorage.setItem(INTRO_SEEN_STORAGE_KEY, "1");
  } catch {
    // Same defensive swallow as readIntroSeen: a failed write just means the
    // intro renders expanded again next time, which is harmless.
  }
}

const IntroLine = (): ReactNode => {
  const [hasSeenIntro, setHasSeenIntro] = useState<boolean | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const seen = readIntroSeen();
    setHasSeenIntro(seen);
    if (!seen) markIntroSeen();
  }, []);

  if (hasSeenIntro === null) return null;

  if (!hasSeenIntro || isExpanded) {
    return <p className="text-body text-ink-secondary">{INTRO_TEXT}</p>;
  }

  return (
    <button
      type="button"
      className="text-left text-body text-ink-secondary underline"
      onClick={() => {
        setIsExpanded(true);
      }}
    >
      {INTRO_DISCLOSURE_LABEL}
    </button>
  );
};

const RequestRowSkeleton = (): ReactNode => (
  <Skeleton shape="card" label="Loading your requests" />
);

const MessagesRequestsScreen = (): ReactNode => {
  const search = useSearch({ from: "/messages_/requests" });
  const navigate = useNavigate({ from: "/messages/requests" });

  const list = useMessageRequestList({});
  const approve = useMessageRequestApprove();
  const decline = useMessageRequestDecline();

  useEffect(() => {
    const code = extractErrorCode(list.data);
    if (code !== null) showApiError(list.data, undefined, list.dataUpdatedAt);
  }, [list.dataUpdatedAt]);

  const items = list.data?.data?.items ?? [];
  const heading = resolveRequestsHeaderTitle(items.length);

  const openChatId =
    search.dialog === REQUEST_PREVIEW_DIALOG ? search.chatId : undefined;
  const openItem = items.find((item) => item.chatId === openChatId);

  const handleOpenPreview = (chatId: string): void => {
    void navigate({
      search: (prev) =>
        openOverlay(prev, {
          kind: "dialog",
          name: REQUEST_PREVIEW_DIALOG,
          params: { chatId },
        }),
    });
  };

  const handleClosePreview = (): void => {
    void navigate({
      search: (prev) => closeOverlay(prev, { clearParams: ["chatId"] }),
      replace: true,
    });
  };

  const openApprovedConversation = (chatId: string): void => {
    void navigate({
      to: "/messages",
      search: { conversationId: chatId },
      replace: true,
    });
  };

  const handleApprove = async (chatId: string): Promise<void> => {
    let response;
    try {
      response = await approve.mutateAsync({ chatId });
    } catch {
      showOfflineError();
      return;
    }
    if (response.error || !response.data?.isApproved) {
      showApiError(response);
      return;
    }
    openApprovedConversation(chatId);
  };

  const handleIgnore = async (chatId: string): Promise<void> => {
    let response;
    try {
      response = await decline.mutateAsync({ chatId });
    } catch {
      showOfflineError();
      return;
    }
    if (response.error) showApiError(response);
  };

  return (
    <SectionScreen heading={heading}>
      <IntroLine />

      {list.isPending ? (
        <div
          className="mx-auto flex w-full max-w-180 flex-col gap-3"
          data-testid="requests-loading"
        >
          <RequestRowSkeleton />
          <RequestRowSkeleton />
          <RequestRowSkeleton />
        </div>
      ) : null}

      {!list.isPending && items.length === 0 ? (
        <div data-testid="requests-empty">
          <SectionEmptyState headline={EMPTY_HEADLINE} body={EMPTY_BODY} />
        </div>
      ) : null}

      {!list.isPending && items.length > 0 ? (
        <div
          className="mx-auto flex w-full max-w-180 flex-col gap-3"
          data-testid="requests-stack"
        >
          {items.map((item) => (
            <RequestRow
              key={item.chatId}
              fromDisplayName={item.fromDisplayName}
              fromAvatarUrl={item.fromAvatarUrl ?? null}
              sharedPoolName={item.sharedPoolName ?? null}
              firstMessagePreview={item.firstMessagePreview}
              requestedAtIso={item.requestedAtIso}
              onPressBody={() => {
                handleOpenPreview(item.chatId);
              }}
              onApprove={() => {
                void handleApprove(item.chatId);
              }}
              onIgnore={() => {
                void handleIgnore(item.chatId);
              }}
              isApproving={
                approve.isPending && approve.variables.chatId === item.chatId
              }
              isIgnoring={
                decline.isPending && decline.variables.chatId === item.chatId
              }
            />
          ))}
        </div>
      ) : null}

      <RequestConversationPanel
        key={openChatId ?? "none"}
        chatId={openChatId}
        previewMessage={
          openItem
            ? {
                fromDisplayName: openItem.fromDisplayName,
                firstMessagePreview: openItem.firstMessagePreview,
              }
            : undefined
        }
        onClose={handleClosePreview}
        onApproved={openApprovedConversation}
        approveMutation={approve}
        declineMutation={decline}
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/messages_/requests")({
  beforeLoad: requireSignedIn,
  validateSearch: validateMessagesRequestsSearch,
  component: MessagesRequestsScreen,
});
